import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Layout from '../components/common/Layout';
import { validateTextArea } from '../utils/formValidator';
import '../styles/AdminManagement.css';

const ServiceRequestManagementPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({ status: '', admin_notes: '' });
  const [validationErrors, setValidationErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [archiveConfirmId, setArchiveConfirmId] = useState(null);
  const [archiveError, setArchiveError] = useState('');
  const [pagination, setPagination] = useState({ currentPage: 1, lastPage: 1, total: 0 });

  useEffect(() => {
    loadRequests();
    loadStats();
  }, [searchQuery, statusFilter, pagination.currentPage]);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter) params.append('status', statusFilter);
      params.append('page', pagination.currentPage);
      params.append('per_page', 10);

      const response = await api.get(`/service-requests?${params.toString()}`);
      if (response.data.success) {
        setRequests(response.data.data);
        setPagination({
          currentPage: response.data.meta.current_page,
          lastPage: response.data.meta.last_page,
          total: response.data.meta.total
        });
      }
    } catch (error) {
      console.error('Failed to load service requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/service-requests/statistics');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleOpenModal = (item) => {
    setSelectedItem(item);
    const isPaymentVerified = item?.service_fee_payment?.status === 'verified';
    setFormData({
      status: isPaymentVerified ? 'completed' : item.status,
      admin_notes: item.admin_notes || '',
      service_fee_amount: item.service_fee_amount || ''
    });
    setFormError('');
    setValidationErrors({});
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    const newErrors = {};

    // Validate status
    if (!formData.status.trim()) {
      newErrors.status = 'Status is required';
    }

    // Validate admin notes if provided
    if (formData.admin_notes.trim()) {
      const notesValidation = validateTextArea(formData.admin_notes, { minLength: 2, maxLength: 2000 });
      if (!notesValidation.valid) {
        newErrors.admin_notes = notesValidation.error;
      }
    }

    // If there are validation errors, show them
    if (Object.keys(newErrors).length > 0) {
      setValidationErrors(newErrors);
      return;
    }

    try {
      await api.put(`/service-requests/${selectedItem.id}`, formData);
      setShowModal(false);
      loadRequests();
      loadStats();
    } catch (error) {
      setFormError(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleDelete = (id) => {
    setArchiveConfirmId(id);
    setArchiveError('');
  };

  const handleConfirmedDelete = async () => {
    try {
      await api.delete(`/service-requests/${archiveConfirmId}`);
      setArchiveConfirmId(null);
      loadRequests();
      loadStats();
    } catch (error) {
      setArchiveError('Failed to archive request. Please try again.');
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = { pending: 'badge-pending', approved: 'badge-approved', rejected: 'badge-rejected', completed: 'badge-completed', cancelled: 'badge-cancelled' };
    return classes[status] || 'badge-pending';
  };

  const formatServiceType = (type) => {
    return type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  return (
    <Layout>
      <div className="admin-management">
          <div className="page-header">
            <div className="header-content">
              <h1>Service Requests</h1>
              <p>Manage member service requests</p>
            </div>
          </div>

          <div className="stats-grid">
            <div className="stat-card total">
              <div className="stat-icon"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/></svg></div>
              <div className="stat-info"><span className="stat-number">{stats.total || 0}</span><span className="stat-label">Total</span></div>
            </div>
            <div className="stat-card pending">
              <div className="stat-icon"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/></svg></div>
              <div className="stat-info"><span className="stat-number">{stats.pending || 0}</span><span className="stat-label">Pending</span></div>
            </div>
            <div className="stat-card approved">
              <div className="stat-icon"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg></div>
              <div className="stat-info"><span className="stat-number">{stats.approved || 0}</span><span className="stat-label">Approved</span></div>
            </div>
            <div className="stat-card completed">
              <div className="stat-icon"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg></div>
              <div className="stat-info"><span className="stat-number">{stats.completed || 0}</span><span className="stat-label">Completed</span></div>
            </div>
          </div>

          <div className="filters-section">
            <div className="search-box">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              <input type="text" placeholder="Search requests..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            </div>
            <div className="filter-dropdown">
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          <div className="data-table-container">
            {loading ? (
              <div className="loading-state"><div className="spinner"></div><p>Loading...</p></div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Requester</th>
                    <th>Service Type</th>
                    <th>Preferred Date</th>
                    <th>Status</th>
                    <th>Submitted</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.length === 0 ? (
                    <tr><td colSpan="6" className="empty-state">No service requests found</td></tr>
                  ) : (
                    requests.map(item => (
                      <tr key={item.id}>
                        <td>
                          <div className="user-cell">
                            <div className="user-avatar">{item.user?.name?.charAt(0).toUpperCase()}</div>
                            <div>
                              <div className="user-name">{item.user?.name}</div>
                              <div className="user-email">{item.user?.email}</div>
                            </div>
                          </div>
                        </td>
                        <td><strong>{formatServiceType(item.service_type)}</strong></td>
                        <td>{item.product_type ? '-' : (item.preferred_date ? new Date(item.preferred_date).toLocaleDateString() : '-')}</td>
                        <td><span className={`status-badge ${getStatusBadgeClass(item.status)}`}>{item.status}</span></td>
                        <td>{new Date(item.created_at).toLocaleDateString()}</td>
                        <td>
                          <div className="action-buttons">
                            <button className="btn-edit" onClick={() => handleOpenModal(item)} title="Process">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                            </button>
                            <button className="btn-archive" onClick={() => handleDelete(item.id)} title="Archive">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 8v13H3V8M1 3h22v5H1zM10 12h4"/></svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
            {pagination.lastPage > 1 && (
              <div className="pagination">
                <button disabled={pagination.currentPage === 1} onClick={() => setPagination(p => ({...p, currentPage: p.currentPage - 1}))}>Previous</button>
                <span>Page {pagination.currentPage} of {pagination.lastPage}</span>
                <button disabled={pagination.currentPage === pagination.lastPage} onClick={() => setPagination(p => ({...p, currentPage: p.currentPage + 1}))}>Next</button>
              </div>
            )}
          </div>
        </div>

        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
              <h2>Process Service Request</h2>
              {formError && <div className="form-error">{formError}</div>}
              
              <div className="request-details">
                <p><strong>Requester:</strong> {selectedItem?.user?.name}</p>
                <p><strong>Service:</strong> {formatServiceType(selectedItem?.service_type || '')}</p>
                {selectedItem?.product_type && (
                  <p><strong>Type:</strong> {selectedItem.product_type}</p>
                )}
                {selectedItem?.price_range && (
                  <p><strong>Price Range:</strong> {selectedItem.price_range}</p>
                )}
                <p><strong>Description:</strong> {selectedItem?.description || 'No description provided'}</p>
                <p><strong>Contact:</strong> {selectedItem?.contact_number || 'N/A'}</p>
                {selectedItem?.preferred_date && !selectedItem?.product_type && (
                  <p><strong>Preferred Date:</strong> {new Date(selectedItem.preferred_date).toLocaleDateString()}</p>
                )}
                {(selectedItem?.body_weight || selectedItem?.body_height || selectedItem?.body_width) && (
                  <div style={{ marginTop: '8px', padding: '10px 14px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px' }}>
                    <p style={{ margin: '0 0 4px', fontWeight: 600, color: '#0369a1', fontSize: '0.85rem' }}>Body Dimensions</p>
                    {selectedItem?.body_weight && <p style={{ margin: '2px 0' }}><strong>Weight:</strong> {selectedItem.body_weight} kg</p>}
                    {selectedItem?.body_height && <p style={{ margin: '2px 0' }}><strong>Height:</strong> {selectedItem.body_height} cm</p>}
                    {selectedItem?.body_width && <p style={{ margin: '2px 0' }}><strong>Width:</strong> {selectedItem.body_width} cm</p>}
                  </div>
                )}
              </div>

              <form onSubmit={handleSubmit}>
                {(() => {
                  const isLocked = selectedItem?.status === 'completed' || selectedItem?.status === 'cancelled';
                  const isPaymentVerified = selectedItem?.service_fee_payment?.status === 'verified';
                  return (
                    <>
                      {isLocked && (
                        <div style={{ background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '8px', padding: '10px 14px', marginBottom: '12px', color: '#6b7280', fontSize: '0.875rem' }}>
                          {selectedItem?.status === 'completed'
                            ? 'This request has been completed and can no longer be modified.'
                            : 'This request was cancelled by the member and can no longer be modified.'}
                        </div>
                      )}
                      {isPaymentVerified && !isLocked && (
                        <div style={{ background: '#fef9c3', border: '1px solid #fde047', borderRadius: '8px', padding: '10px 14px', marginBottom: '12px', color: '#92400e', fontSize: '0.875rem' }}>
                          The service fee has been paid and verified. You can only mark this request as completed.
                        </div>
                      )}
                      <div className="form-group">
                        <label>Status *</label>
                        <select 
                          value={formData.status} 
                          onChange={(e) => {
                            setFormData({...formData, status: e.target.value});
                            const error = !e.target.value.trim() ? 'Status is required' : null;
                            setValidationErrors(prev => {
                              const updated = { ...prev };
                              if (error) { updated.status = error; } else { delete updated.status; }
                              return updated;
                            });
                          }} 
                          className={validationErrors.status ? 'error' : ''}
                          required
                          disabled={isLocked}
                        >
                          {isPaymentVerified ? (
                            <option value="completed">Completed</option>
                          ) : (
                            <>
                              <option value="pending">Pending</option>
                              <option value="approved">Approved</option>
                              <option value="rejected">Rejected</option>
                              <option value="completed">Completed</option>
                            </>
                          )}
                        </select>
                        {validationErrors.status && (
                          <small className="error-message">{validationErrors.status}</small>
                        )}
                      </div>
                      <div className="form-group">
                        <label>Admin Notes</label>
                        <textarea 
                          rows="3" 
                          value={formData.admin_notes} 
                          onChange={(e) => {
                            setFormData({...formData, admin_notes: e.target.value});
                            let error = null;
                            if (e.target.value.trim()) {
                              const r = validateTextArea(e.target.value, { minLength: 2, maxLength: 2000 });
                              if (!r.valid) error = r.error;
                            }
                            setValidationErrors(prev => {
                              const updated = { ...prev };
                              if (error) { updated.admin_notes = error; } else { delete updated.admin_notes; }
                              return updated;
                            });
                          }} 
                          className={validationErrors.admin_notes ? 'error' : ''}
                          placeholder="Add notes about this request..." 
                          disabled={isLocked}
                        />
                        {validationErrors.admin_notes && (
                          <small className="error-message">{validationErrors.admin_notes}</small>
                        )}
                      </div>
                      {(formData.status === 'approved' || (selectedItem?.status !== 'pending' && selectedItem?.service_fee_amount)) && (
                        <div className="form-group">
                          <label>Service Fee (PHP)</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.service_fee_amount}
                            onChange={(e) => setFormData({...formData, service_fee_amount: e.target.value})}
                            placeholder="e.g. 500.00 — leave blank if no charge"
                            disabled={selectedItem?.status !== 'pending' || isLocked}
                            style={(selectedItem?.status !== 'pending' || isLocked) ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                          />
                          <small style={{ color: '#6b7280', fontSize: '0.8rem' }}>
                            {selectedItem?.status !== 'pending'
                              ? 'Service fee is locked once the request is no longer pending.'
                              : 'A payment due will be created for the member when a fee is set.'}
                          </small>
                        </div>
                      )}
                      <div className="form-actions">
                        <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                        {!isLocked && (
                          <button type="submit" className="btn-submit" disabled={Object.keys(validationErrors).length > 0}>Update Status</button>
                        )}
                      </div>
                    </>
                  );
                })()}
              </form>
            </div>
          </div>
        )}

      {archiveConfirmId !== null && (
        <div
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
          onClick={() => { setArchiveConfirmId(null); setArchiveError(''); }}
        >
          <div
            style={{ background: '#fff', borderRadius: '16px', padding: '40px 36px', maxWidth: '420px', width: '90%', textAlign: 'center', position: 'relative', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}
            onClick={e => e.stopPropagation()}
          >
            <button onClick={() => { setArchiveConfirmId(null); setArchiveError(''); }} style={{ position: 'absolute', top: '14px', right: '16px', background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}>&times;</button>
            <div style={{ fontSize: '44px', marginBottom: '12px' }}>🗄️</div>
            <h2 style={{ margin: '0 0 8px', fontSize: '22px', fontWeight: '700', color: '#111827' }}>Archive This Request?</h2>
            <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '6px' }}>Are you sure you want to archive this service request?</p>
            <p style={{ color: '#ef4444', fontSize: '13px', fontWeight: '600', marginBottom: '24px' }}>This cannot be undone.</p>
            {archiveError && <p style={{ color: '#ef4444', fontSize: '13px', marginBottom: '16px', padding: '8px 12px', background: '#fef2f2', borderRadius: '6px' }}>{archiveError}</p>}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button onClick={() => { setArchiveConfirmId(null); setArchiveError(''); }} style={{ padding: '10px 28px', borderRadius: '8px', border: '1px solid #d1d5db', background: '#f9fafb', color: '#374151', fontSize: '15px', cursor: 'pointer', fontWeight: '500' }}>Cancel</button>
              <button onClick={handleConfirmedDelete} style={{ padding: '10px 28px', borderRadius: '8px', border: 'none', background: '#1a472a', color: '#fff', fontSize: '15px', cursor: 'pointer', fontWeight: '600' }}>Yes, Archive</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ServiceRequestManagementPage;